/**
 * SubsidyReportExportDialog - CSV export of the Sozialdepartement queue (#2003),
 * the month-end record the office keeps in Excel: one row per item, filtered on
 * the item's due date and its state. A filter form: it writes nothing.
 */
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatLocalDate } from '@/utils/dateUtils';
import { useDownloadSubsidyReportExport } from '@/hooks/useSubsidyReports';
import { getStateLabel } from '@/types/workflow';

const ALL = 'all';
const STATES = ['to_file', 'filed', 'approved', 'rejected', 'cancelled'] as const;

function monthBounds(): { from: Date; to: Date } {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
  };
}

export function SubsidyReportExportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { i18n } = useLingui();
  const [from, setFrom] = useState<Date | undefined>(monthBounds().from);
  const [to, setTo] = useState<Date | undefined>(monthBounds().to);
  const [state, setState] = useState<string>(ALL);
  const download = useDownloadSubsidyReportExport();

  useEffect(() => {
    if (!open) return;
    const { from: f, to: t } = monthBounds();
    setFrom(f);
    setTo(t);
    setState(ALL);
  }, [open]);

  const orderError = !!from && !!to && from.getTime() > to.getTime();
  const busy = download.isPending;

  const run = () => {
    if (orderError) return;
    download.mutate(
      {
        from: from ? formatLocalDate(from) : undefined,
        to: to ? formatLocalDate(to) : undefined,
        state: state === ALL ? undefined : state,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md" data-testid="subsidy-report-export-dialog">
        <DialogHeader>
          <DialogTitle>{i18n._(msg({ id: 'subsidyReport.export.title', message: 'Export subsidy reports' }))}</DialogTitle>
          <DialogDescription>
            {i18n._(
              msg({
                id: 'subsidyReport.export.description',
                message: 'One row per item with the values for Kollab, its state and the dates. CSV for Excel.',
              }),
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="subsidy-report-export-from">
                {i18n._(msg({ id: 'subsidyReport.export.from', message: 'Due from' }))}
              </Label>
              <DatePicker id="subsidy-report-export-from" value={from} onChange={setFrom} showClearButton />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subsidy-report-export-to">
                {i18n._(msg({ id: 'subsidyReport.export.to', message: 'Due until' }))}
              </Label>
              <DatePicker id="subsidy-report-export-to" value={to} onChange={setTo} showClearButton aria-invalid={orderError} />
            </div>
          </div>
          {orderError ? (
            <p className="text-sm text-destructive" role="alert">
              {i18n._(msg({ id: 'subsidyReport.export.order', message: '"Due from" must not be after "due until".' }))}
            </p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="subsidy-report-export-state">
              {i18n._(msg({ id: 'subsidyReport.export.state', message: 'State' }))}
            </Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="subsidy-report-export-state" data-testid="subsidy-report-export-state">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{i18n._(msg({ id: 'subsidyReport.export.allStates', message: 'All states' }))}</SelectItem>
                {STATES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {getStateLabel(i18n, s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {i18n._(msg({ id: 'common.cancel', message: 'Cancel' }))}
          </Button>
          <Button type="button" disabled={orderError || busy} onClick={run} data-testid="subsidy-report-export-run">
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {i18n._(msg({ id: 'subsidyReport.export.run', message: 'Download CSV' }))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SubsidyReportExportDialog;
